import { useQuery } from "@tanstack/react-query";
import React, { createContext, ReactNode, useContext } from "react";

type PlayerMap = Record<string, { firstName?: string; lastName?: string }>;

type PlayerStatsContextValue = {
  players: PlayerMap;
  loading: boolean;
  error: any;
  getPlayerName: (
    playerId?: string | null,
    options?: { full?: boolean },
  ) => string | null;
};

export const PlayerStatsContext = createContext<PlayerStatsContextValue | null>(
  null,
);

export function usePlayerStats() {
  const ctx = useContext(PlayerStatsContext);
  if (!ctx)
    throw new Error("usePlayerStats must be used within PlayerStatsProvider");
  return ctx;
}

async function fetchPlayersForTeam(teamId: string) {
  const url = `https://www.backend.ufastats.com/api/v1/players?years=2025&teamIDs=${encodeURIComponent(teamId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`players fetch failed: ${res.status}`);
  const json = await res.json();
  // assume players are in json.data or json.players
  const arr = json.data || json.players || json || [];
  return Array.isArray(arr) ? arr : [];
}

export function PlayerStatsProvider({
  homeTeamId,
  awayTeamId,
  children,
}: {
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  children: ReactNode;
}) {
  const enabled = Boolean(homeTeamId || awayTeamId);

  const { data, isLoading, error } = useQuery<PlayerMap | undefined>({
    queryKey: ["playerStats", homeTeamId, awayTeamId],
    queryFn: async () => {
      const teams = [homeTeamId, awayTeamId].filter(Boolean) as string[];
      const results = await Promise.all(
        teams.map((t) => fetchPlayersForTeam(t)),
      );
      // merge into a map keyed by playerId; try multiple possible id fields
      const map: PlayerMap = {};
      results.forEach((arr) => {
        arr.forEach((p: any) => {
          const id = p.playerID;
          if (!id) return;
          map[String(id)] = {
            firstName: p.firstName,
            lastName: p.lastName,
          };
        });
      });
      return map;
    },
    enabled: enabled,
  });

  const players: PlayerMap = (data as PlayerMap) || {};

  const getPlayerName = (
    playerId?: string | null,
    options?: { full?: boolean },
  ) => {
    if (!playerId) return null;
    const p = players[String(playerId)];
    if (!p) return null;
    const first = p.firstName || "";
    const last = p.lastName || "";
    if (options?.full) return `${first} ${last}`.trim();
    return first || last || null;
  };

  const value: PlayerStatsContextValue = {
    players,
    loading: Boolean(isLoading),
    error,
    getPlayerName,
  };

  return (
    <PlayerStatsContext.Provider value={value}>
      {children}
    </PlayerStatsContext.Provider>
  );
}
